import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function ProductView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { projects = [], id } = location.state || {};
  const project = projects.find((p) => p.id === id) || projects[0];

  const projectImages = Array.isArray(project.images) && project.images.length > 0
    ? project.images
    : project.images
      ? [project.images]
      : [];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, src: null });
  const [isImageLoading, setIsImageLoading] = useState(true);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (projectImages.length > 1) {
      const nextIndex = (currentImageIndex + 1) % projectImages.length;
      const prevIndex = (currentImageIndex - 1 + projectImages.length) % projectImages.length;

      const preloadImage = (src) => {
        const img = new Image();
        img.src = src;
      };

      preloadImage(projectImages[nextIndex]);
      preloadImage(projectImages[prevIndex]);
    }
  }, [currentImageIndex, projectImages]);



  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const disableMotion = prefersReducedMotion || isMobile;

  if (!project) {
    return (
      <div className="container" style={{ padding: '2rem' }}>
        <h2>No project found.</h2>
        <button className="btn" onClick={() => navigate('/products')}>Back to Projects</button>
      </div>
    );
  }

  const openLightbox = (src) => setLightbox({ open: true, src });
  const closeLightbox = () => setLightbox({ open: false, src: null });

  return (
    <div className="min-h-screen bg-slate-900 mt-20">
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Back Button - Top Left */}


          {/* Project Title - Top Center */}
          <motion.div
            className="text-left mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: disableMotion ? 0 : 0.6, ease: "easeOut" }}
          >
            <h1 className="text-3xl lg:text-5xl font-extrabold tracking-tight text-white drop-shadow-sm">
              {project.title}
            </h1>
          </motion.div>


          {/* Image and Overview Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <motion.div
              className="lg:col-span-2 relative cursor-pointer group overflow-hidden rounded-2xl bg-slate-800"
              style={{
                height: '500px',
              }}
              onClick={() => openLightbox(projectImages[currentImageIndex])}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: disableMotion ? 0 : 1.2, ease: "easeOut" }}
            >
              {isImageLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-0">
                  <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                </div>
              )}

              <motion.img
                key={currentImageIndex}
                src={projectImages[currentImageIndex]}
                alt={project.title}
                className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: isImageLoading ? 0 : 1 }}
                transition={{ duration: disableMotion ? 0 : 0.5 }}
                onLoad={() => setIsImageLoading(false)}
                loading="eager"
              />

              {/* Gradient Overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30 pointer-events-none" />

              {/* Modern Navigation Arrows */}
              <motion.button
                className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 text-white rounded-full transition-all duration-300 flex items-center justify-center group shadow-xl z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsImageLoading(true);
                  setCurrentImageIndex((prev) => (prev === 0 ? projectImages.length - 1 : prev - 1));
                }}
                whileHover={disableMotion ? {} : { scale: 1.1 }}
                whileTap={disableMotion ? {} : { scale: 0.95 }}
              >
                <svg className="w-6 h-6 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
              <motion.button
                className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 text-white rounded-full transition-all duration-300 flex items-center justify-center group shadow-xl z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsImageLoading(true);
                  setCurrentImageIndex((prev) => (prev === projectImages.length - 1 ? 0 : prev + 1));
                }}
                whileHover={disableMotion ? {} : { scale: 1.1 }}
                whileTap={disableMotion ? {} : { scale: 0.95 }}
              >
                <svg className="w-6 h-6 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </motion.button>
              {/* Modern Image Indicators */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
                {projectImages.map((_, index) => (
                  <motion.button
                    key={index}
                    className={`h-1 rounded-full transition-all duration-300 ${index === currentImageIndex
                      ? 'bg-white w-8 shadow-lg'
                      : 'bg-white/40 w-6 hover:bg-white/60'
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsImageLoading(true);
                      setCurrentImageIndex(index);
                    }}
                    whileHover={{ scale: 1.1 }}
                  />
                ))}
              </div>

              {/* Top Controls */}
              <div className="absolute top-6 left-6 right-6 flex justify-end items-center">
                <div className="px-3 py-1.5 bg-black/30 backdrop-blur-md text-white rounded-full text-sm font-medium">
                  {currentImageIndex + 1} / {projectImages.length}
                </div>
              </div>
            </motion.div>

            {/* Project Details - Right Side */}
            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: disableMotion ? 0 : 0.8, delay: 0.3 }}
            >
              <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-lg p-6 border border-white/10" style={{ height: '500px' }}>
                <div className="h-full flex flex-col justify-center">
                  {/* Location and Year Badges */}
                  <div className="flex flex-wrap gap-3 mb-6">
                    <div className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="font-medium">{project.location}</span>
                    </div>

                    <div className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">Completed {project.completedYear}</span>
                    </div>
                  </div>

                  {/* Project Overview */}
                  <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    Project Overview
                  </h2>

                  <p className="text-slate-200 leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: disableMotion ? 0 : 0.6 }}
          >
            <button
              onClick={() => navigate('/products')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-secondary text-white hover:text-primary-dark rounded-lg transition-all duration-300 font-medium hover:shadow-md"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Projects
            </button>
          </motion.div>
        </div>

        {/* Lightbox */}
        <AnimatePresence>
          {lightbox.open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeLightbox}
              className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
              style={{
                backdropFilter: 'blur(10px)',
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", duration: disableMotion ? 0 : 0.3 }}
                className="relative max-w-7xl mx-auto p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={lightbox.src}
                  alt="Preview"
                  className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                />
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-2xl p-2 transition-all"
                >
                  ×
                </button>
                <div className="absolute bottom-4 left-4 right-4 flex justify-center gap-2">
                  {projectImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setLightbox({ open: true, src: img })}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${lightbox.src === img ? 'border-amber-500 scale-110' : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                    >
                      <img src={img} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section >
    </div >
  );
}
